import {
  copyText,
  detachLayoutProps,
  useIsDesktop,
  useIsMobile,
  useWSSnackbar,
  WSBadge,
  WSButton,
  WSCircle,
  WSDivider,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSIconName,
  WSModal,
  WSScreen,
  WSText
} from "@wingspanhq/fe-component-library";
import { IActivityContext } from "@wingspanhq/users";
import classNames from "classnames";
import { truncate } from "lodash";
import * as React from "react";
import { TouchEventHandler, useEffect } from "react";
import { useHistory } from "react-router";
import { NavLink, useLocation } from "react-router-dom";
import { AskYourAdvocateWidget } from "../../MemberOnboarding/components/AskYourAdvocateWidget/AskYourAdvocateWidget";
import { createPPL } from "../../PersonalPayLink/utils";
import { useUserId } from "../../query/hooks/helpers";
import {
  experimentFlags,
  useFeatureFlags
} from "../../query/hooks/useFeatureFlags";
import { useActivities, useUserProfile } from "../../query/users/queries";
import { useAuthorizedScopeGroups } from "../../Settings/utils/teamUtils";
import {
  isIAPathMember,
  isUnderWriterMember
} from "../../utils/growthAttribution";
import { toCamelCase } from "../../utils/stringHelper";
import { HeadwayNotification } from "../HeadwayNotification";
import { HelpCenter } from "../HelpCenter/HelpCenter";
import { Tooltip } from "../Tooltip";
import { New } from "./New";
import styles from "./styles.module.scss";
import { SwitchAccounts } from "./SwitchAccounts";
import { UserAccountType } from "@wingspanhq/users/dist/lib/interfaces/newUser";
import { useResume1099SetupFlow } from "../../hooks/useResumeSetupFlow";

export const ASK_YOUR_ADVOCATE_SIDE_NAV_MODAL =
  "ASK_YOUR_ADVOCATE_SIDE_NAV_MODAL";

export const AskYourAdvocateModal: React.FC = () => (
  <WSModal
    name={ASK_YOUR_ADVOCATE_SIDE_NAV_MODAL}
    size="S"
    key="modal"
    className={styles.advocateWidgetModal}
  >
    <AskYourAdvocateWidget />
  </WSModal>
);

enum ExpandableSideNavMenuOptions {
  Payments = "Payments",
  MyFinances = "MyFinances",
  ResourceHub = "ResourceHub",
  HelpCenter = "HelpCenter",
  Settings = "Settings",
  Benefits = "Benefits",
  Organization = "Organization"
}

interface SideNavHeaderProps extends Omit<SideNavProps, "hideGettingStarted"> {}

export const SideNavHeader: React.FC<SideNavHeaderProps> = ({
  isDrawerOpened,
  setIsDrawerOpened = () => {},
  isSideNavOpened,
  setIsSideNavOpened = () => {},
  ...otherProps
}) => {
  const userId = useUserId();
  const isDesktop = useIsDesktop();
  const activityQuery = useActivities(userId);
  const { layoutProps } = detachLayoutProps(otherProps);
  const getPartnerLogoStyles = () => {
    const isUnderwriterMember = isUnderWriterMember(
      activityQuery.data?.context as IActivityContext
    );
    const isMemberFromIABenefits = isIAPathMember(
      activityQuery.data?.context as IActivityContext
    );
    if (isUnderwriterMember) {
      return styles.partnerUSALogo;
    } else if (isMemberFromIABenefits) {
      return styles.partnerIABenefitsLogo;
    }
    return isDesktop ? styles.navigationLogo : styles.mobileNavigationLogo;
  };
  return (
    <>
      <WSScreen.Mobile>
        <WSFlexBox.CenterY
          justify="space-between"
          className={styles.navigationHeader}
          {...layoutProps}
        >
          <WSElement
            className={styles.hamburgerMenuIconContainer}
            onClick={() => setIsDrawerOpened(!isDrawerOpened)}
            data-testid="hamburgerMenuIcon"
          >
            <WSIcon
              name={isDrawerOpened ? "close" : "hamburgerMenu1"}
              color="grey700"
            />
          </WSElement>
          <WSElement ml="-XS" as="span" className={getPartnerLogoStyles()} />
          <HelpCenter place="header">
            {onClick => (
              <WSFlexBox.Center p="M" ml="-S" mr="-M" onClick={onClick}>
                <WSIcon name="question" color="grey700" size="M" />
              </WSFlexBox.Center>
            )}
          </HelpCenter>
        </WSFlexBox.CenterY>
      </WSScreen.Mobile>
      <WSScreen.TabletAndDesktop>
        <WSFlexBox.CenterY
          justify="space-between"
          className={styles.navigationHeader}
          {...layoutProps}
        >
          <WSElement as="span" className={getPartnerLogoStyles()} />
          <WSIcon
            name="hamburgerMenu1"
            color="grey700"
            onClick={() => {
              setIsSideNavOpened(!isSideNavOpened);
            }}
            data-testid="hamburgerMenuIcon"
          />
        </WSFlexBox.CenterY>
      </WSScreen.TabletAndDesktop>
    </>
  );
};

interface CollapsibleMenuProps extends WSElementProps {
  icon: WSIconName;
  label: string;
  isExpanded: boolean;
  isSideNavOpened?: boolean;
  blocked?: boolean;
}

const CollapsibleMenu: React.FC<CollapsibleMenuProps> = ({
  icon,
  label,
  isExpanded,
  children,
  onClick,
  isSideNavOpened,
  blocked
}) => {
  const isMobile = useIsMobile();
  const content = (
    <WSFlexBox.CenterY
      px="M"
      className={styles.collapsibleMenu}
      onClick={onClick}
      wrap="nowrap"
      data-testid={toCamelCase([label, "menu"])}
    >
      <WSIcon
        name={icon}
        color={isSideNavOpened ? "grey700" : blocked ? "grey500" : "grey700"}
      />
      <WSText.Paragraph
        ml="M"
        color={blocked ? "grey500" : "grey700"}
        className={styles.label}
      >
        {label}
        {blocked && (isSideNavOpened || isMobile) ? (
          <WSElement as="span" className={styles.icon} ml="S">
            <WSIcon name="lock" color="grey600" size="XS" />
          </WSElement>
        ) : null}
      </WSText.Paragraph>
      <WSIcon
        name={isExpanded ? "chevronUp" : "chevronDown"}
        size="XXS"
        color="grey700"
        data-testid="menu-arrow-indicator"
      />
    </WSFlexBox.CenterY>
  );
  return (
    <>
      {isSideNavOpened || isMobile ? (
        content
      ) : (
        <Tooltip
          className={styles.tooltip}
          trigger={content}
          showInfoIcon={false}
          placement="right-start"
        >
          <WSText.Paragraph color="white" className={styles.label}>
            {label}
          </WSText.Paragraph>
        </Tooltip>
      )}
      <WSFlexBox
        className={classNames(styles.subMenuContainer, {
          [styles.expanded]: isExpanded
        })}
        direction="column"
      >
        {children}
      </WSFlexBox>
    </>
  );
};

interface NormalLinkOptionProps {
  to: string;
  testId: string;
  icon: WSIconName;
  label: string;
  isSideNavOpened?: boolean;
}

const NormalLinkOption: React.FC<NormalLinkOptionProps> = ({
  to,
  icon,
  label,
  testId,
  isSideNavOpened
}) => {
  const isMobile = useIsMobile();
  const content = (
    <NavLink
      activeClassName={styles.activeNavLink}
      className={styles.navLink}
      to={to}
      data-testid={testId}
    >
      <WSFlexBox.CenterY px="M" className={styles.defaultMainMenuOption}>
        <WSIcon name={icon} color="grey700" />
        <WSText.Paragraph ml="M" color="grey700" className={styles.label}>
          {label}
        </WSText.Paragraph>
      </WSFlexBox.CenterY>
    </NavLink>
  );
  return isSideNavOpened || isMobile ? (
    content
  ) : (
    <Tooltip trigger={content} showInfoIcon={false} placement="right-start">
      <WSText.Paragraph color="white" className={styles.label}>
        {label}
      </WSText.Paragraph>
    </Tooltip>
  );
};

interface NormalOptionProps {
  onClick: () => void;
  label: string;
  icon: WSIconName;
  isSideNavOpened?: boolean;
  badge?: string;
  name: string;
}
const NormalOption: React.FC<NormalOptionProps> = ({
  onClick,
  label,
  icon,
  isSideNavOpened,
  badge,
  name
}) => {
  const isMobile = useIsMobile();
  const content = (
    <WSFlexBox.CenterY
      px="M"
      className={styles.defaultMainMenuOption}
      onClick={onClick}
      data-testid={name}
    >
      <WSIcon name={icon} color="grey700" />
      <WSText.Paragraph ml="M" color="grey700" className={styles.label}>
        {label}
      </WSText.Paragraph>
      {badge && (isSideNavOpened || isMobile) ? (
        <WSBadge
          text={badge}
          className={styles.badge}
          backgroundColor="green100"
        />
      ) : null}
    </WSFlexBox.CenterY>
  );
  return isSideNavOpened || isMobile ? (
    content
  ) : (
    <Tooltip trigger={content} showInfoIcon={false} placement="right-start">
      <WSText.Paragraph color="white" className={styles.label}>
        {label}
      </WSText.Paragraph>
    </Tooltip>
  );
};

const WalletLink: React.FC<{
  isSideNavOpened: boolean;
  flags?: typeof experimentFlags;
}> = ({ isSideNavOpened, flags }) => {
  const isMobile = useIsMobile();

  return flags?.wallet ? (
    <NavLink
      activeClassName={styles.activeNavLink}
      className={styles.navLink}
      to="/member/wallet"
      data-testid="myFinancesWallet"
    >
      <WSFlexBox.CenterY className={styles.subMenuOption}>
        <WSText.Label color="grey700">Wingspan Wallet</WSText.Label>
        {isSideNavOpened || isMobile ? (
          <WSBadge
            text="New"
            className={styles.badge}
            backgroundColor="green100"
          />
        ) : null}
      </WSFlexBox.CenterY>
    </NavLink>
  ) : null;
};

interface SideNavMenuProps {
  isSideNavOpened?: boolean;
  setIsSideNavOpened?: (isDrawerOpened: boolean) => void;
  setIsDrawerOpened?: (isDrawerOpened: boolean) => void;
}

const SideNavMenu: React.FC<SideNavMenuProps> = ({
  isSideNavOpened,
  setIsSideNavOpened = () => {},
  setIsDrawerOpened = () => {}
}) => {
  const history = useHistory();
  const [topMenu, setTopMenu] = React.useState<string[]>([]);
  const [bottomMenu, setBottomMenu] = React.useState<string[]>([]);

  const { openSnackbar } = useWSSnackbar();
  const isMobile = useIsMobile();
  const location = useLocation();
  const userId = useUserId();
  const user = useUserProfile(userId);
  const qActivity = useActivities(userId);

  const {
    isAdminSessionExists,
    isRequestingUserSessionExists,
    hasAdminScope,
    hasPaymentsScope,
    hasCollaboratorsScope,
    hasFinancesScope,
    hasDocumentsScope
  } = useAuthorizedScopeGroups();

  React.useEffect(() => {
    if (!isSideNavOpened) {
      setTopMenu([]);
      setBottomMenu([]);
    }
  }, [isSideNavOpened]);

  const featureFlagsQuery = useFeatureFlags();
  const { flow: setup1099Flow } = useResume1099SetupFlow();

  const onExpandMainMenuItem = (
    item: ExpandableSideNavMenuOptions,
    top = true
  ) => {
    if (!isSideNavOpened) {
      setIsSideNavOpened(true);
    }
    if (top) {
      if (topMenu.includes(item)) {
        setTopMenu(topMenu.filter(menu => menu !== item));
      } else {
        setTopMenu([...topMenu, item]);
        setBottomMenu([]);

        // TODO: Disabling it so I comment it. We mostly uncomment it soon.
        // if (!isMobile) {
        //   // NOTE: Navigate to first submenu option on expand
        //   history.push("/member/invoices");
        // }
      }
    } else {
      if (bottomMenu.includes(ExpandableSideNavMenuOptions.Settings)) {
        setBottomMenu(
          bottomMenu.filter(
            menu => menu !== ExpandableSideNavMenuOptions.Settings
          )
        );
      } else {
        setBottomMenu([...bottomMenu, ExpandableSideNavMenuOptions.Settings]);
        setTopMenu([]);
        // if (!isMobile) {
        //   // NOTE: Navigate to first submenu option on expand
        //   history.push("/member/settings/account");
        // }
      }
    }
  };

  const is1099FilingSetupComplete = setup1099Flow?.complete;

  const isEnterpriseUser =
    user.data?.settings?.userAccountType === UserAccountType.enterprise;

  return (
    <WSElement
      as="nav"
      className={classNames(isMobile ? styles.navMobile : styles.nav, {
        [styles.collapsed]: !isMobile && !isSideNavOpened
      })}
      data-testuseremail={user.data?.email}
    >
      <WSFlexBox direction="column" className={styles.navItemsContainer}>
        <WSScreen.TabletAndDesktop>
          <SideNavHeader
            p="M"
            isSideNavOpened={isSideNavOpened}
            setIsSideNavOpened={setIsSideNavOpened}
          />
        </WSScreen.TabletAndDesktop>

        {isMobile ? (
          <>
            <WSFlexBox.CenterY>
              {user.data?.profile.photo ? (
                <WSCircle.Image
                  m="M"
                  size="XL"
                  image={user.data?.profile.photo}
                />
              ) : (
                <WSCircle.Image
                  m="M"
                  size="XL"
                  image={
                    require("../../assets/images/defaultUserPhoto.svg").default
                  }
                />
              )}
              <WSElement>
                <WSText.Paragraph
                  color="grey800"
                  style={{ fontSize: "17px", lineHeight: "24px" }}
                >
                  <b>
                    {truncate(
                      `${user.data?.profile.firstName || ""} ${user.data
                        ?.profile.lastName || ""}`,
                      {
                        length: 20,
                        separator: " ",
                        omission: ""
                      }
                    )}
                  </b>
                </WSText.Paragraph>
                {qActivity.data?.flows.settingsFoundingMemberInterest
                  ?.complete ? (
                  <WSText.Label color="grey600">
                    Early Access Member 🌟
                  </WSText.Label>
                ) : (
                  <WSButton.Link
                    onClick={async () => {
                      history.push("/member/settings/account/early-access");
                      setIsDrawerOpened(false);
                    }}
                  >
                    Request Early Access
                  </WSButton.Link>
                )}
              </WSElement>
            </WSFlexBox.CenterY>
            <WSDivider mb="M" />
          </>
        ) : null}

        {isRequestingUserSessionExists || isAdminSessionExists ? null : (
          <SwitchAccounts
            isSideNavOpened={!!isSideNavOpened}
            setIsSideNavOpened={
              isMobile ? setIsDrawerOpened : setIsSideNavOpened
            }
          />
        )}
        <New
          isSideNavOpened={!!isSideNavOpened}
          setIsSideNavOpened={isMobile ? setIsDrawerOpened : setIsSideNavOpened}
        />

        <NormalLinkOption
          to="/member/dashboard"
          testId="memberGettingStarted"
          label="Getting Started"
          icon="travel"
          isSideNavOpened={isSideNavOpened}
        />

        <WSFlexBox direction="column" className={styles.topNavItems} mt="M">
          {hasPaymentsScope || hasCollaboratorsScope ? (
            <CollapsibleMenu
              isSideNavOpened={isSideNavOpened}
              icon="moneyRounded"
              label="Payments"
              isExpanded={topMenu.includes(
                ExpandableSideNavMenuOptions.Payments
              )}
              onClick={() =>
                onExpandMainMenuItem(ExpandableSideNavMenuOptions.Payments)
              }
            >
              {hasPaymentsScope && (
                <NavLink
                  activeClassName={styles.activeNavLink}
                  className={styles.navLink}
                  to="/member/invoices"
                  data-testid="paymentsInvoicing"
                  exact
                >
                  <WSFlexBox.CenterY className={styles.subMenuOption}>
                    <WSText.Label color="grey700">Invoicing</WSText.Label>
                  </WSFlexBox.CenterY>
                </NavLink>
              )}
              {hasPaymentsScope && (
                <NavLink
                  activeClassName={styles.activeNavLink}
                  className={styles.navLink}
                  to="/member/personal-pay-link"
                  data-testid="paymentsPayLink"
                >
                  <WSFlexBox.CenterY className={styles.subMenuOption}>
                    <WSText.Label color="grey700">Pay Link</WSText.Label>
                  </WSFlexBox.CenterY>
                </NavLink>
              )}
              {hasPaymentsScope && (
                <NavLink
                  activeClassName={styles.activeNavLink}
                  className={styles.navLink}
                  to="/member/invoices/payables"
                  data-testid="paymentsPayables"
                >
                  <WSFlexBox.CenterY className={styles.subMenuOption}>
                    <WSText.Label color="grey700">Payables</WSText.Label>
                  </WSFlexBox.CenterY>
                </NavLink>
              )}
              {hasCollaboratorsScope && (
                <NavLink
                  activeClassName={styles.activeNavLink}
                  className={styles.navLink}
                  to="/member/invoices/contacts"
                  data-testid="paymentsContacts"
                >
                  <WSFlexBox.CenterY className={styles.subMenuOption}>
                    <WSText.Label color="grey700">Contacts</WSText.Label>
                  </WSFlexBox.CenterY>
                </NavLink>
              )}
              {hasPaymentsScope && featureFlagsQuery.data?.nec1099Filing && (
                <NavLink
                  activeClassName={styles.activeNavLink}
                  className={styles.navLink}
                  to={
                    is1099FilingSetupComplete
                      ? "/member/1099-filing/dashboard"
                      : "/member/1099-filing/preview"
                  }
                  data-testid="NEC1099Filing"
                  exact
                >
                  <WSFlexBox.CenterY className={styles.subMenuOption}>
                    <WSText.Label color="grey700">1099-NEC Filing</WSText.Label>
                  </WSFlexBox.CenterY>
                </NavLink>
              )}
            </CollapsibleMenu>
          ) : null}
          {hasFinancesScope ? (
            <CollapsibleMenu
              isSideNavOpened={isSideNavOpened}
              icon="institution"
              label="My Finances"
              isExpanded={topMenu.includes(
                ExpandableSideNavMenuOptions.MyFinances
              )}
              onClick={() =>
                onExpandMainMenuItem(ExpandableSideNavMenuOptions.MyFinances)
              }
            >
              <WalletLink
                isSideNavOpened={true}
                flags={featureFlagsQuery.data}
              />
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/bookkeeping"
                data-testid="myFinancesIncomeAndExpenses"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Income & Expenses</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/taxes"
                data-testid="myFinancesTaxWithholdings"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Tax Withholdings</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
            </CollapsibleMenu>
          ) : null}
          {hasDocumentsScope ? (
            <CollapsibleMenu
              isSideNavOpened={isSideNavOpened}
              icon="resourceHub"
              label="Resource Hub"
              isExpanded={topMenu.includes(
                ExpandableSideNavMenuOptions.ResourceHub
              )}
              onClick={() =>
                onExpandMainMenuItem(ExpandableSideNavMenuOptions.ResourceHub)
              }
            >
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/documents"
                data-testid="resourceHubMyDocuments"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">My Documents</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/knowledge-center"
                data-testid="knowledgeCenter"
                exact
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Knowledge Center</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
            </CollapsibleMenu>
          ) : null}
          {hasAdminScope ? (
            <CollapsibleMenu
              isSideNavOpened={isSideNavOpened}
              icon="heart"
              label="Member Benefits"
              isExpanded={topMenu.includes(
                ExpandableSideNavMenuOptions.Benefits
              )}
              onClick={() =>
                onExpandMainMenuItem(ExpandableSideNavMenuOptions.Benefits)
              }
            >
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/benefits/my-benefits"
                data-testid="benefits-my-benefits"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Premium Perks</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/benefits/additional-coverage/all"
                data-testid="benefits-additional-coverage"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Insurance</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
            </CollapsibleMenu>
          ) : null}
          {hasAdminScope && isEnterpriseUser ? (
            <CollapsibleMenu
              isSideNavOpened={isSideNavOpened}
              icon="community"
              label="Organization"
              isExpanded={topMenu.includes(
                ExpandableSideNavMenuOptions.Organization
              )}
              onClick={() =>
                onExpandMainMenuItem(ExpandableSideNavMenuOptions.Organization)
              }
            >
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/organization/accounts"
                data-testid="organizationAccounts"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Accounts</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
            </CollapsibleMenu>
          ) : null}
        </WSFlexBox>
        <WSDivider my="M" />
        {hasAdminScope ? (
          <WSFlexBox
            direction="column"
            className={styles.bottomNavItems}
            wrap="nowrap"
          >
            <HeadwayNotification
              isMobile={isMobile}
              isSideNavOpened={isSideNavOpened}
              kind="nav"
            />
            <CollapsibleMenu
              isSideNavOpened={isSideNavOpened}
              icon="gears"
              label="Settings"
              isExpanded={bottomMenu.includes(
                ExpandableSideNavMenuOptions.Settings
              )}
              onClick={() =>
                onExpandMainMenuItem(
                  ExpandableSideNavMenuOptions.Settings,
                  false
                )
              }
            >
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/settings/account"
                data-testid="settingsAccount"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">
                    Account & Membership
                  </WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/settings/personal-info"
                data-testid="settingsPersonalInfo"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Personal Info</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/settings/business-info"
                data-testid="settingsBusinessInfo"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Business Info</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/settings/payment-methods"
                data-testid="settingsIntegrations"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Payment Methods</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
              {featureFlagsQuery.data?.qboOnboarding ? (
                <NavLink
                  activeClassName={styles.activeNavLink}
                  className={styles.navLink}
                  to="/member/settings/integrations"
                  data-testid="settingsIntegrations"
                >
                  <WSFlexBox.CenterY className={styles.subMenuOption}>
                    <WSText.Label color="grey700">Integrations</WSText.Label>
                  </WSFlexBox.CenterY>
                </NavLink>
              ) : null}
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/settings/tax-profile"
                data-testid="settingsTaxProfile"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Tax Profile</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/settings/notifications"
                data-testid="settingsNotifications"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Notifications</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/settings/team"
                data-testid="settingsTeam"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Team</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/settings/tokens"
                data-testid="settingsDeveloper"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Developer</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
              <NavLink
                activeClassName={styles.activeNavLink}
                className={styles.navLink}
                to="/member/settings/payroll"
                data-testid="settingsPayroll"
              >
                <WSFlexBox.CenterY className={styles.subMenuOption}>
                  <WSText.Label color="grey700">Payroll</WSText.Label>
                </WSFlexBox.CenterY>
              </NavLink>
            </CollapsibleMenu>
            <NormalLinkOption
              to="/sign-out"
              testId="logout"
              label="Log out"
              icon="logout"
              isSideNavOpened={isSideNavOpened}
            />
          </WSFlexBox>
        ) : (
          <WSFlexBox
            direction="column"
            className={styles.bottomNavItems}
            wrap="nowrap"
          >
            <NormalLinkOption
              to="/sign-out"
              testId="logout"
              label="Log out"
              icon="logout"
              isSideNavOpened={isSideNavOpened}
            />
          </WSFlexBox>
        )}
        <WSScreen.Mobile>
          {user.data?.tag ? (
            <WSFlexBox.Center className={styles.copyLinkWrapper}>
              <WSElement
                as="button"
                className={styles.copyLink}
                px="XL"
                py="S"
                onClick={async () => {
                  await copyText(createPPL(user.data?.tag as string));

                  openSnackbar({
                    type: "success",
                    message: "Copied",
                    duration: 3000
                  });
                }}
              >
                {truncate(createPPL(user.data?.tag), { length: 25 })}
              </WSElement>
            </WSFlexBox.Center>
          ) : null}
        </WSScreen.Mobile>
      </WSFlexBox>
    </WSElement>
  );
};

interface SideNavProps extends WSElementProps {
  isDrawerOpened?: boolean;
  setIsDrawerOpened?: (isDrawerOpened: boolean) => void;
  isSideNavOpened?: boolean;
  setIsSideNavOpened?: (isSideNavOpened: boolean) => void;
  desktopTopPadding?: number;
}

export const SideNav: React.FC<SideNavProps> = ({
  isDrawerOpened,
  setIsDrawerOpened = () => {},
  isSideNavOpened,
  setIsSideNavOpened,
  desktopTopPadding
}) => {
  const [touchStart, setTouchStart] = React.useState(0);
  const [touchEnd, setTouchEnd] = React.useState(0);

  const handleTouchStart: TouchEventHandler<HTMLElement> = event => {
    const clientX = event.targetTouches[0].clientX;

    setTouchStart(clientX);
    setTouchEnd(clientX);
  };

  const handleTouchMove: TouchEventHandler<HTMLElement> = event => {
    const clientX = event.targetTouches[0].clientX;

    setTouchEnd(clientX);
  };

  const handleTouchEnd: TouchEventHandler<HTMLElement> = () => {
    if (touchStart - touchEnd > 100) {
      setIsDrawerOpened(false);
    }
  };

  return (
    <>
      <WSScreen.Mobile>
        <WSElement
          onTouchEnd={handleTouchEnd}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onClick={event => {
            const target = event.target as HTMLElement;

            if (target === event.currentTarget) {
              setIsDrawerOpened(false);
            }

            if (target.tagName.toLowerCase() === "a" || target.closest("a")) {
              setIsDrawerOpened(false);
            }
          }}
          className={classNames(styles.mobileNavContainer, {
            [styles.mobileNavOpened]: isDrawerOpened
          })}
        >
          <SideNavMenu setIsDrawerOpened={setIsDrawerOpened} />
        </WSElement>
      </WSScreen.Mobile>
      <WSScreen.TabletAndDesktop>
        <WSElement
          className={styles.desktopNavContainer}
          style={{ top: desktopTopPadding }}
        >
          <SideNavMenu
            isSideNavOpened={isSideNavOpened}
            setIsSideNavOpened={setIsSideNavOpened}
          />
        </WSElement>
      </WSScreen.TabletAndDesktop>
    </>
  );
};
